// src/components/EventList.js - With Running Order
import React, { useState } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { getDancersList, normalizeEvent } from '../utils/fieldCompatibility';
import { formatTime, calculateEventEndTime, getEventsWithStartTimes } from '../utils/startTimeCalculator';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './EventList.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

/**
 * Helper function to detect non-dance events
 * @param {Object} event - Event object
 * @returns {boolean} True if this is a non-dance event
 */
const isNonDanceEvent = (event) => {
  // Check explicit flag first (for future events created with the new form)
  if (event.isNonDanceEvent === true) {
    return true;
  }

  // PRIMARY INDICATOR: Check if event has dances (this is the most reliable indicator)
  const hasDances = (event.dances && event.dances.length > 0);

  // Check if the name suggests it's a break/non-dance event
  const nameIndicatesNonDance = event.name && (
    event.name.toUpperCase().includes('BREAK') ||
    event.name.toUpperCase().includes('LUNCH') ||
    event.name.toUpperCase().includes('INTERVAL') ||
    event.name.toUpperCase().includes('ENTERTAINMENT') ||
    event.name.toUpperCase().includes('OPENING') ||
    event.name.toUpperCase().includes('CLOSING')
  );

  // Primary logic: If it has dances and name doesn't indicate non-dance, it's a dance event
  if (hasDances && !nameIndicatesNonDance) {
    return false; // This is definitely a dance event
  }

  // If name indicates non-dance, treat as non-dance event regardless of other factors
  if (nameIndicatesNonDance) {
    return true;
  }

  // Legacy fallback detection for edge cases
  const hasNoDances = (!event.dances || event.dances.length === 0);
  const hasNullOrMissingDanceInfo = (
    (event.ageGroup === null || event.ageGroup === undefined || event.ageGroup === "") &&
    (event.age_group === null || event.age_group === undefined || event.age_group === "") &&
    (event.level === null || event.level === undefined || event.level === "") &&
    (event.danceStyle === null || event.danceStyle === undefined || event.danceStyle === "") &&
    (event.dance_style === null || event.dance_style === undefined || event.dance_style === "")
  );
  const hasNoDancerInfo = (!event.dancers || event.dancers.length === 0);

  return hasNoDances && hasNullOrMissingDanceInfo && hasNoDancerInfo;
};

const EventList = ({ events, loading, onSelectEvent, selectedEventId, onDeleteEvent, onReorderEvents, showDeleteOnly, competitionStartTime, filterBroadcastLive = false }) => {
  const { selectedCompetition } = useCompetition();
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);
  const [newPosition, setNewPosition] = useState('');
  const [showDancersModal, setShowDancersModal] = useState(false);
  const [selectedEventForDancers, setSelectedEventForDancers] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [reorderSource, setReorderSource] = useState(null); // Source event for click-to-reorder
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [moveTargetEvent, setMoveTargetEvent] = useState(null);
  const [isProcessingMove, setIsProcessingMove] = useState(false);

  // Loading states for individual operations
  const [processingEventId, setProcessingEventId] = useState(null); // Track which event is being processed
  const [processingOperation, setProcessingOperation] = useState(null); // Track type of operation (delete, move-up, move-down)
  const [processingMessage, setProcessingMessage] = useState(''); // Custom processing message


  if (loading) {
    return <div className="event-list-loading">Loading events...</div>;
  }

  if (!events || !Array.isArray(events)) {
    return <div className="event-list-empty">No events available. Please try refreshing.</div>;
  }

  // Note: Removed global processing overlay to prevent blocking screen interactions
  // Individual buttons show their own loading states now

  if (events.length === 0) {
    return <div className="event-list-empty">
      {filterBroadcastLive
        ? "No live events found. Events must have broadcast status = 'live' to appear here."
        : "No events found. Create one to get started."
      }
    </div>;
  }

  // Normalize events for consistent field access
  const normalizedEvents = events.map(event => normalizeEvent(event));

  // Filter events for broadcast state if requested (Screen Management only)
  const broadcastFilteredEvents = filterBroadcastLive
    ? normalizedEvents.filter(event => event.broadcast_state === 'live')
    : normalizedEvents;

  // Sort events by item number
  const sortedEvents = [...broadcastFilteredEvents].sort((a, b) => {
    const itemA = a.itemNumber || 999;
    const itemB = b.itemNumber || 999;
    return itemA - itemB;
  });

  // Calculate start times if competition start time is available
  const eventsWithStartTimes = competitionStartTime
    ? getEventsWithStartTimes(sortedEvents, competitionStartTime)
    : sortedEvents;

  // Filter events based on search query
  const filteredEvents = eventsWithStartTimes.filter(event => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();

    // Search by item number
    const itemNumber = (event.itemNumber || (sortedEvents.indexOf(event) + 1)).toString();
    if (itemNumber.includes(query)) return true;

    // Search by event name
    if (event.name && event.name.toLowerCase().includes(query)) return true;

    return false;
  });

  // Handle case where no events pass the broadcast filter
  if (filterBroadcastLive && sortedEvents.length === 0) {
    return (
      <div className="event-list-container">
        <div className="event-list-header">
          <h2>Live Events Only</h2>
          <p className="running-order-hint">
            Only events with broadcast status = 'live' are shown • Click item number to view and manage dancers
          </p>
        </div>
        <div className="event-list-scrollable">
          <div className="event-list-empty">
            No live events found. Events must have broadcast status = 'live' to appear here.
          </div>
        </div>
      </div>
    );
  }

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === dropIndex) {
      return;
    }

    const newEvents = [...filteredEvents];
    const [movedEvent] = newEvents.splice(draggedItem, 1);
    newEvents.splice(dropIndex, 0, movedEvent);

    // Update item numbers
    const updatedEvents = newEvents.map((event, index) => ({
      ...event,
      itemNumber: index + 1
    }));

    if (onReorderEvents) {
      onReorderEvents(updatedEvents);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;

    const event = filteredEvents[index];
    setProcessingEventId(event.id);
    setProcessingOperation('move-up');
    setProcessingMessage(`Moving "${event.name}" up...`);

    try {
      const newEvents = [...filteredEvents];
      [newEvents[index - 1], newEvents[index]] = [newEvents[index], newEvents[index - 1]];

      const updatedEvents = newEvents.map((evt, idx) => ({
        ...evt,
        itemNumber: idx + 1
      }));

      if (onReorderEvents) {
        await onReorderEvents(updatedEvents);
      }
    } finally {
      setProcessingEventId(null);
      setProcessingOperation(null);
      setProcessingMessage('');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === filteredEvents.length - 1) return;

    const event = filteredEvents[index];
    setProcessingEventId(event.id);
    setProcessingOperation('move-down');
    setProcessingMessage(`Moving "${event.name}" down...`);

    try {
      const newEvents = [...filteredEvents];
      [newEvents[index], newEvents[index + 1]] = [newEvents[index + 1], newEvents[index]];

      const updatedEvents = newEvents.map((evt, idx) => ({
        ...evt,
        itemNumber: idx + 1
      }));

      if (onReorderEvents) {
        await onReorderEvents(updatedEvents);
      }
    } finally {
      setProcessingEventId(null);
      setProcessingOperation(null);
      setProcessingMessage('');
    }
  };

  const handleDelete = async (eventId, eventName) => {
    setProcessingEventId(eventId);
    setProcessingOperation('delete');
    setProcessingMessage(`Deleting "${eventName}"...`);

    try {
      if (onDeleteEvent) {
        await onDeleteEvent(eventId, eventName);
      }
    } finally {
      setProcessingEventId(null);
      setProcessingOperation(null);
      setProcessingMessage('');
    }
  };

  const handlePositionEdit = (eventId) => {
    setEditingPosition(eventId);
    const event = filteredEvents.find(e => e.id === eventId);
    setNewPosition(event?.itemNumber?.toString() || '');
  };

  const handlePositionSave = (eventId) => {
    const pos = parseInt(newPosition, 10);

    if (isNaN(pos) || pos < 1 || pos > filteredEvents.length) {
      alert(`Please enter a valid position between 1 and ${filteredEvents.length}`);
      return;
    }

    const currentIndex = filteredEvents.findIndex(e => e.id === eventId);
    const targetIndex = pos - 1;

    if (currentIndex === targetIndex) {
      setEditingPosition(null);
      return;
    }

    const newEvents = [...filteredEvents];
    const [movedEvent] = newEvents.splice(currentIndex, 1);
    newEvents.splice(targetIndex, 0, movedEvent);

    const updatedEvents = newEvents.map((event, index) => ({
      ...event,
      itemNumber: index + 1
    }));

    if (onReorderEvents) {
      onReorderEvents(updatedEvents);
    }

    setEditingPosition(null);
    setNewPosition('');
  };

  const handlePositionCancel = () => {
    setEditingPosition(null);
    setNewPosition('');
  };

  const handleItemNumberClick = (event, e) => {
    e.stopPropagation();
    // Always show dancers modal when clicking item number
    setSelectedEventForDancers(event);
    setShowDancersModal(true);
  };

  const handleRemoveDancerFromEvent = async (dancerId) => {
    if (!selectedEventForDancers) return;

    const confirmRemove = window.confirm('Are you sure you want to remove this dancer from the event?');
    if (!confirmRemove) return;

    try {
      const updatedDancers = getDancersList(selectedEventForDancers).filter(d => {
        const currentDancerId = d.coupleId || d.couple_id;
        return currentDancerId !== dancerId;
      });

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${selectedEventForDancers.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dancers: updatedDancers })
      });

      if (!response.ok) {
        throw new Error('Failed to remove dancer');
      }

      // Update local state
      setSelectedEventForDancers({
        ...selectedEventForDancers,
        dancers: updatedDancers
      });

      // Force page refresh to update all views
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error('Error removing dancer:', err);
      alert('Failed to remove dancer. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowDancersModal(false);
    setSelectedEventForDancers(null);
  };

  // Click-to-reorder handlers
  const handleEventClick = (event, e) => {
    // Prevent triggering when clicking on buttons or inputs
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT' ||
        e.target.closest('button') || e.target.closest('input')) {
      return;
    }

    if (!onReorderEvents) return; // Only enable reordering if reordering is allowed

    if (!reorderSource) {
      // First click - select source event
      setReorderSource(event);
    } else if (reorderSource.id === event.id) {
      // Clicking the same event - deselect
      setReorderSource(null);
    } else {
      // Second click - show confirmation dialog
      setMoveTargetEvent(event);
      setShowMoveConfirmation(true);
    }
  };

  const confirmMove = async () => {
    if (!reorderSource || !moveTargetEvent) return;

    setIsProcessingMove(true);

    try {
      // Get current indices
      const sourceIndex = filteredEvents.findIndex(e => e.id === reorderSource.id);
      const targetIndex = filteredEvents.findIndex(e => e.id === moveTargetEvent.id);

      if (sourceIndex === -1 || targetIndex === -1) return;

      // Create new array with reordered events
      const reorderedEvents = [...filteredEvents];
      const [movedEvent] = reorderedEvents.splice(sourceIndex, 1);
      reorderedEvents.splice(targetIndex, 0, movedEvent);

      // Update item numbers based on new order
      const updatedEvents = reorderedEvents.map((event, index) => ({
        ...event,
        itemNumber: index + 1
      }));

      // Call the reorder handler
      if (onReorderEvents) {
        await onReorderEvents(updatedEvents);
      }

      // Reset state
      setReorderSource(null);
      setMoveTargetEvent(null);
      setShowMoveConfirmation(false);
    } catch (err) {
      console.error('Error reordering events:', err);
      alert('Failed to reorder events. Please try again.');
    } finally {
      setIsProcessingMove(false);
    }
  };

  const cancelMove = () => {
    setReorderSource(null);
    setMoveTargetEvent(null);
    setShowMoveConfirmation(false);
    setIsProcessingMove(false);
  };

  // PDF generation function
  const generateRunningOrderPDF = () => {
    try {
      const doc = new jsPDF();
      const eventsWithTimes = getEventsWithStartTimes(
        searchQuery ? filteredEvents : sortedEvents,
        competitionStartTime
      );

      // Add title
      doc.setFontSize(18);
      doc.text('Running Order', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });

      // Add competition info and generation time
      doc.setFontSize(12);
      doc.text(`Organizer: Federation of Dance Sport South Africa`, 20, 28);
      doc.text(`Competition: ${selectedCompetition?.name || 'N/A'}`, 20, 35);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 42);

      if (competitionStartTime) {
        doc.text(`Start Time: ${formatTime(competitionStartTime)}`, 20, 49);
      }

      // Add summary
      const totalEvents = eventsWithTimes.length;
      const totalDancers = eventsWithTimes.reduce((sum, event) => {
        const dancers = getDancersList(event);
        return sum + (Array.isArray(dancers) ? dancers.length : 0);
      }, 0);

      doc.text(`Total Events: ${totalEvents}`, 20, 56);
      doc.text(`Total Dancers: ${totalDancers}`, 20, 63);

      // Prepare table data with special handling for non-dance events
      const tableData = eventsWithTimes.map((event, index) => {
        const dancers = getDancersList(event);
        const dancerCount = Array.isArray(dancers) ? dancers.length : 0;
        const startTime = event.startTime
          ? formatTime(event.startTime)
          : 'TBD';
        const estimatedEndTime = event.startTime && event.estimatedDuration
          ? formatTime(new Date(event.startTime.getTime() + event.estimatedDuration * 1000))
          : 'TBD';

        // Check if this is a non-dance event
        const isNonDance = isNonDanceEvent(event);

        if (isNonDance) {
          // For non-dance events, create a special row with only the event name
          return [
            {
              content: '',
              styles: { cellWidth: 15, fillColor: [240, 240, 240] } // #
            },
            {
              content: event.name || 'Untitled Event',
              styles: {
                cellWidth: 140, // Combined width of all columns (20+35+30+30+15)
                fillColor: [240, 240, 240],
                fontStyle: 'bold',
                halign: 'center'
              }
            },
            {
              content: '',
              styles: { cellWidth: 20, fillColor: [240, 240, 240] } // Round Type
            },
            {
              content: '',
              styles: { cellWidth: 35, fillColor: [240, 240, 240] } // Dancers
            },
            {
              content: '',
              styles: { cellWidth: 30, fillColor: [240, 240, 240] } // Start Time
            },
            {
              content: '',
              styles: { cellWidth: 30, fillColor: [240, 240, 240] } // End Time
            },
            {
              content: '',
              styles: { cellWidth: 15, fillColor: [240, 240, 240] } // Heats
            }
          ];
        } else {
          // Regular dance events
          return [
            event.item_number || index + 1,
            event.name || 'Untitled Event',
            event.roundType || event.round_type || 'Round',
            dancerCount,
            startTime,
            estimatedEndTime,
            event.num_heats || 'N/A'
          ];
        }
      });

      // Add table using autoTable
      autoTable(doc, {
        head: [['#', 'Event Name', 'Round', 'Dancers', 'Start Time', 'End Time', 'Heats']],
        body: tableData,
        startY: 75,
        theme: 'grid',
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 9
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 15 }, // #
          1: { cellWidth: 60 }, // Event Name (will be overridden for non-dance events)
          2: { cellWidth: 20 }, // Round Type
          3: { cellWidth: 35 }, // Dancers (increased width)
          4: { cellWidth: 30 }, // Start Time
          5: { cellWidth: 30 }, // End Time
          6: { cellWidth: 15 }  // Heats
        },
        margin: { left: 10, right: 10 },
        // Custom hooks for row styling
        didParseCell: (data) => {
          // Skip processing for header rows
          if (data.row.section === 'head') return;

          const eventIndex = data.row.index;
          const event = eventsWithTimes[eventIndex];

          if (event && isNonDanceEvent(event)) {
            // Non-dance events are already handled in the tableData object styles
            return;
          }
        },
        didDrawRow: (data) => {
          // Custom drawing for non-dance events
          if (data.row.section === 'body') {
            const eventIndex = data.row.index;
            const event = eventsWithTimes[eventIndex];

            if (event && isNonDanceEvent(event)) {
              // Get row dimensions
              const rowHeight = data.row.height;
              const rowY = data.row.y;

              // Calculate approximate table width based on configured column widths
              const tableWidth = 15 + 60 + 20 + 35 + 30 + 30 + 15; // Sum of all column widths

              // Get the event name
              const eventNameCell = data.row.cells[1]; // Event Name is at index 1
              const cellText = eventNameCell && eventNameCell.raw && eventNameCell.raw.content ? eventNameCell.raw.content : eventNameCell.text;
              const textContent = typeof cellText === 'string' ? cellText : '';

              // Draw a continuous gray background for the entire row
              doc.setFillColor(240, 240, 240);
              doc.rect(20, rowY, tableWidth, rowHeight, 'F');

              if (textContent) {
                // Calculate center position across the row width (accounting for margins)
                const textWidth = doc.getTextWidth(textContent);
                const xPos = 20 + (tableWidth - textWidth) / 2; // 20px left margin + centered text
                const yPos = rowY + rowHeight / 2 + 3;

                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(0, 0, 0); // Ensure black text on gray background
                doc.text(textContent, xPos, yPos);
              }
            }
          }
        },
        willDrawCell: (data) => {
          // Hide borders for non-dance events
          if (data.row.section === 'body') {
            const eventIndex = data.row.index;
            const event = eventsWithTimes[eventIndex];

            if (event && isNonDanceEvent(event)) {
              data.cell.styles.lineWidth = 0;
              data.cell.styles.lineColor = [255, 255, 255];
            }
          }
        }
      });

      // Add footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 10, { align: 'center' });

        // Add Flokraft attribution in smallest font
        doc.setFontSize(7);
        doc.setTextColor(128, 128, 128); // Gray color for subtlety
        doc.text('Generated by Flokraft Ultimate DanceSport Management System © 2025',
                doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 5, { align: 'center' });
        doc.setTextColor(0, 0, 0); // Reset to black
      }

      // Generate filename
      const competitionName = selectedCompetition?.name || 'Competition';
      const sanitizedCompetitionName = competitionName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      const filename = `running_order_${sanitizedCompetitionName}_${timestamp}.pdf`;

      // Save PDF
      doc.save(filename);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="event-list-container">
      {/* Fixed Header */}
      <div className="event-list-header">
        <h2>{filterBroadcastLive ? 'Live Events Only' : 'Running Order'}</h2>
        <p className="running-order-hint">
          {filterBroadcastLive
            ? "Only events with broadcast status = 'live' are shown • Click item number to view and manage dancers"
            : onReorderEvents
            ? "Click an event to select, then click another to move it there • Drag to reorder • Click item number to view/manage dancers"
            : "Click item number to view and manage dancers"}
        </p>

        {/* Search Bar */}
        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by item number or event name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="search-results-info">
              Found {filteredEvents.length} of {sortedEvents.length} events
            </div>
          )}
        </div>

        {/* PDF Export Button - Only show for full running order, not live events */}
        {!filterBroadcastLive && (
          <div className="pdf-export-section">
            <button
              className="pdf-export-btn"
              onClick={generateRunningOrderPDF}
              title="Export running order as PDF"
            >
              <span className="pdf-icon">📄</span>
              Export PDF
            </button>
          </div>
        )}

        </div>

      {/* Scrollable Content */}
      <div className="event-list-scrollable">
        {searchQuery && filteredEvents.length === 0 ? (
          <div className="search-no-results">
            <p>No events found matching "{searchQuery}"</p>
            <button
              className="clear-search-btn"
              onClick={() => setSearchQuery('')}
            >
              Clear search
            </button>
          </div>
        ) : (
          <ul className="event-list">
          {filteredEvents.map((event, index) => (
          <li
            key={event.id}
            className={`event-list-item ${event.id === selectedEventId ? 'selected' : ''} ${
              reorderSource?.id === event.id ? 'reorder-source' : ''
            } ${isNonDanceEvent(event) ? 'non-dance-event' : ''}`}
            draggable={!editingPosition && !!onReorderEvents}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onClick={(e) => handleEventClick(event, e)}
          >
            {isNonDanceEvent(event) ? (
              /* Non-Dance Event Layout */
              <>
                {/* Item Number */}
                <div className="item-number-section">
                  {editingPosition === event.id ? (
                    <div className="position-editor">
                      <input
                        type="number"
                        value={newPosition}
                        onChange={(e) => setNewPosition(e.target.value)}
                        min="1"
                        max={filteredEvents.length}
                        className="position-input"
                        autoFocus
                      />
                      <button
                        className="position-save-btn"
                        onClick={() => handlePositionSave(event.id)}
                        title="Save position"
                      >
                        ✓
                      </button>
                      <button
                        className="position-cancel-btn"
                        onClick={handlePositionCancel}
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      className="item-number"
                      onClick={(e) => handleItemNumberClick(event, e)}
                      title="Click to view and manage dancers"
                    >
                      {event.itemNumber || index + 1}
                    </div>
                  )}
                </div>

                {/* Non-Dance Event Content - Centered, All Caps */}
                <div className="non-dance-event-content">
                  <div className="non-dance-event-name">
                    {event.name.toUpperCase()}
                  </div>
                  {event.startTime && (
                    <div className="non-dance-event-time" title="Estimated start time">
                      🕐 {formatTime(event.startTime)}
                    </div>
                  )}
                </div>

                {/* Control Buttons */}
                <div className="event-controls">
                  {onReorderEvents && (
                    <div className="reorder-buttons">
                      <button
                        className={`move-btn move-up ${processingEventId === filteredEvents[index]?.id && processingOperation === 'move-up' ? 'processing' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveUp(index);
                        }}
                        disabled={index === 0 || processingEventId !== null}
                        title="Move up"
                      >
                        {processingEventId === filteredEvents[index]?.id && processingOperation === 'move-up' ? (
                          <span className="btn-spinner">⏳</span>
                        ) : (
                          '↑'
                        )}
                      </button>
                      <button
                        className={`move-btn move-down ${processingEventId === filteredEvents[index]?.id && processingOperation === 'move-down' ? 'processing' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveDown(index);
                        }}
                        disabled={index === filteredEvents.length - 1 || processingEventId !== null}
                        title="Move down"
                      >
                        {processingEventId === filteredEvents[index]?.id && processingOperation === 'move-down' ? (
                          <span className="btn-spinner">⏳</span>
                        ) : (
                          '↓'
                        )}
                      </button>
                    </div>
                  )}

                  {onDeleteEvent && (
                    <button
                      className={`delete-event-btn ${processingEventId === event.id && processingOperation === 'delete' ? 'processing' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(event.id, event.name);
                      }}
                      disabled={event.active || processingEventId !== null}
                      title={event.active ? "Cannot delete active event" : "Delete event"}
                    >
                      {processingEventId === event.id && processingOperation === 'delete' ? (
                        <span className="btn-spinner">⏳</span>
                      ) : (
                        '×'
                      )}
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* Regular Dance Event Layout */
              <>
                {/* Item Number */}
                <div className="item-number-section">
                  {editingPosition === event.id ? (
                    <div className="position-editor">
                      <input
                        type="number"
                        value={newPosition}
                        onChange={(e) => setNewPosition(e.target.value)}
                        min="1"
                        max={filteredEvents.length}
                        className="position-input"
                        autoFocus
                      />
                      <button
                        className="position-save-btn"
                        onClick={() => handlePositionSave(event.id)}
                        title="Save position"
                      >
                        ✓
                      </button>
                      <button
                        className="position-cancel-btn"
                        onClick={handlePositionCancel}
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      className="item-number"
                      onClick={(e) => handleItemNumberClick(event, e)}
                      title="Click to view and manage dancers"
                    >
                      {event.itemNumber || index + 1}
                    </div>
                  )}
                </div>

                {/* Event Content */}
                <div
                  className="event-list-item-content"
                  onClick={() => onSelectEvent && onSelectEvent(event)}
                >
                  <div className="event-list-item-name">{event.name}</div>
                  <div className="event-list-item-details">
                    <span className="event-round-type">{event.roundType}</span>
                    <span className="event-dancers-count">
                      {getDancersList(event)?.length || 0} Dancers
                    </span>
                    {event.startTime && (
                      <span className="event-start-time" title="Estimated start time">
                        🕐 {formatTime(event.startTime)}
                      </span>
                    )}
                  </div>
                  {event.active && <span className="event-active-badge">ACTIVE</span>}
                </div>

                {/* Control Buttons */}
                <div className="event-controls">
                  {onReorderEvents && (
                    <div className="reorder-buttons">
                      <button
                        className={`move-btn move-up ${processingEventId === filteredEvents[index]?.id && processingOperation === 'move-up' ? 'processing' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveUp(index);
                        }}
                        disabled={index === 0 || processingEventId !== null}
                        title="Move up"
                      >
                        {processingEventId === filteredEvents[index]?.id && processingOperation === 'move-up' ? (
                          <span className="btn-spinner">⏳</span>
                        ) : (
                          '↑'
                        )}
                      </button>
                      <button
                        className={`move-btn move-down ${processingEventId === filteredEvents[index]?.id && processingOperation === 'move-down' ? 'processing' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveDown(index);
                        }}
                        disabled={index === filteredEvents.length - 1 || processingEventId !== null}
                        title="Move down"
                      >
                        {processingEventId === filteredEvents[index]?.id && processingOperation === 'move-down' ? (
                          <span className="btn-spinner">⏳</span>
                        ) : (
                          '↓'
                        )}
                      </button>
                    </div>
                  )}

                  {onDeleteEvent && (
                    <button
                      className={`delete-event-btn ${processingEventId === event.id && processingOperation === 'delete' ? 'processing' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(event.id, event.name);
                      }}
                      disabled={event.active || processingEventId !== null}
                      title={event.active ? "Cannot delete active event" : "Delete event"}
                    >
                      {processingEventId === event.id && processingOperation === 'delete' ? (
                        <span className="btn-spinner">⏳</span>
                      ) : (
                        '×'
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </li>
        ))}
        </ul>
        )}
      </div>

      {/* Dancers Modal */}
      {showDancersModal && selectedEventForDancers && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dancers in Event</h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-event-info">
              <span className="modal-item-number">{selectedEventForDancers.itemNumber}</span>
              <span className="modal-event-name">{selectedEventForDancers.name}</span>
            </div>
            <div className="modal-body">
              {selectedEventForDancers.dancers && selectedEventForDancers.dancers.length > 0 ? (
                <div className="modal-dancers-list">
                  {selectedEventForDancers.dancers.map((dancer, index) => (
                    <div key={index} className="modal-dancer-item">
                      <div className="modal-dancer-number">{dancer.clothNumber}</div>
                      <div className="modal-dancer-info">
                        <div className="modal-dancer-name">{dancer.name}</div>
                        <div className="modal-dancer-country">{dancer.country}</div>
                      </div>
                      <button
                        className="modal-remove-btn"
                        onClick={() => handleRemoveDancerFromEvent(dancer.coupleId)}
                        title="Remove from event"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="modal-empty-message">No dancers allocated to this event</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Move Confirmation Modal */}
      {showMoveConfirmation && reorderSource && moveTargetEvent && (
        <div className={`modal-overlay move-confirmation-overlay ${isProcessingMove ? 'processing' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div className={`move-confirmation-modal ${isProcessingMove ? 'processing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="move-confirmation-header">
              <h3>Confirm Move Operation</h3>
              <button
                className="close-confirmation-btn"
                onClick={cancelMove}
                title="Cancel"
              >
                ×
              </button>
            </div>

            <div className="move-confirmation-content">
              <p className="move-confirmation-question">
                Move this event:
              </p>
              <div className="move-source-event">
                <strong>{reorderSource.itemNumber}. {reorderSource.name}</strong>
              </div>

              <p className="move-confirmation-to">
                to position:
              </p>
              <div className="move-target-event">
                <strong>{moveTargetEvent.itemNumber}. {moveTargetEvent.name}</strong>
              </div>

              <div className="move-confirmation-note">
                <p>This will update the running order and renumber all events.</p>
              </div>
            </div>

            <div className="move-confirmation-actions">
              <button
                className="cancel-move-btn"
                onClick={cancelMove}
                disabled={isProcessingMove}
              >
                Cancel
              </button>
              <button
                className={`confirm-move-btn ${isProcessingMove ? 'processing' : ''}`}
                onClick={confirmMove}
                disabled={isProcessingMove}
              >
                {isProcessingMove ? (
                  <>
                    <span className="processing-spinner"></span>
                    Processing...
                  </>
                ) : (
                  'Confirm Move'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventList;
